import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (_e) {
    return '';
  }
};

const Blog = () => {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts`)
      .then((r) => setPosts(r.data?.posts || []))
      .catch(() => setPosts([]));
  }, []);

  // Auto-discover the RSS feed in feed readers (NetNewsWire, Feedly, etc.).
  useEffect(() => {
    const rssHref = `${process.env.REACT_APP_BACKEND_URL}/api/blog/rss.xml`;
    let link = document.head.querySelector('link[rel="alternate"][type="application/rss+xml"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('type', 'application/rss+xml');
      link.setAttribute('title', 'Forge Ledger Journal');
      document.head.appendChild(link);
    }
    link.setAttribute('href', rssHref);
    return () => {
      // Clean the tag up when the user leaves /blog so other pages don't
      // inherit it.
      const stale = document.head.querySelector('link[rel="alternate"][type="application/rss+xml"]');
      if (stale) stale.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#020617] flex flex-col" data-testid="blog-list-page">
      <Seo
        title="The Forge journal — UK personal-finance notes"
        description="UK-focused notes on budgeting, forecasting, and the everyday money traps Forge Ledger was built to spot."
        path="/blog"
      />
      <div className="flex-1">
      <header className="max-w-3xl mx-auto px-6 sm:px-8 pt-16 pb-10">
        <Link to="/" className="text-sm text-[#0052FF] hover:underline" data-testid="blog-back-home">← Forge Ledger</Link>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-4">The Forge journal</h1>
        <p className="text-[#64748B] mt-3 max-w-xl leading-relaxed">
          UK-focused notes on budgeting, forecasting, and the everyday money traps we built Forge Ledger to spot.
        </p>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 pb-24">
        {posts === null && (
          <div className="text-[#64748B] text-sm">Loading…</div>
        )}
        {posts?.length === 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center" data-testid="blog-empty">
            <p className="text-[#475569]">No posts yet. Check back soon — the first wave goes live next week.</p>
          </div>
        )}
        {posts?.length > 0 && (
          <ul className="space-y-6" data-testid="blog-post-list">
            {posts.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="block bg-white border border-[#E2E8F0] rounded-xl p-6 sm:p-8 hover:border-[#CBD5E1] hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all"
                  data-testid={`blog-post-card-${p.slug}`}
                >
                  <div className="flex items-center gap-3 text-xs text-[#64748B] mb-3">
                    <span>{formatDate(p.published_at)}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {p.reading_time_minutes} min read
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#020617] mb-2">{p.title}</h2>
                  <p className="text-sm text-[#475569] leading-relaxed mb-4">{p.excerpt}</p>
                  <span className="text-sm text-[#0052FF] font-medium inline-flex items-center gap-1">
                    Read more <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default Blog;
